import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { DropdownThreeDotsButton } from '@hivebrite/design-system/Dropdown';
import { Link } from '@hivebrite/design-system/Link';
import { breakText, responsive } from '@hivebrite/design-system/Theme';

import Reactions from 'hb-react/frontoffice/components/Reactions';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import RsvpDropdown from 'hb-react/shared/apps/Events/components/RsvpDropdown';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import Columns, { Column } from 'hb-react/shared/components/Columns';
import { ContentRecommendation } from 'hb-react/shared/components/ContentRecommendation';
import toggleLike from 'hb-react/shared/components/DesignSystem/Reactions/utils/toggleLike';
import EventDate from 'hb-react/shared/components/EventDate';
import UserSnapshot from 'hb-react/shared/components/UserSnapshot';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import { ENTITY_TYPES } from 'hb-react/shared/utils/entitiesContentData/constants';
import onEnterPress from 'hb-react/shared/utils/onEnterPress';
import { completeName } from 'helpers/name';

import Attendee from '../Attendee';

const DISPLAY_ATTENDEES_COUNT = 4;

const EventDisplay = ({ actionsItems }) => {
  const { event } = useEventContext();
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const { currentUser } = useDefaultAppContexts();
  const { currentNetwork } = useDefaultAppContexts();
  const { topic } = useTopicContext();

  const [displayAttendeesCount, setDisplayAttendeesCount] = useState(
    DISPLAY_ATTENDEES_COUNT,
  );

  const { flagUjAiContentRecommendationForumEvents } = useFeatureFlag();

  const { attending_users: attendingUsers = [] } = event;

  // Content recommendation hasn't been implemented for groups yet
  const isContentRecommendationVisible =
    flagUjAiContentRecommendationForumEvents &&
    currentUser &&
    !topic?.id &&
    currentNetwork?.network_configuration?.ai_content_recommendation?.enabled;

  const shouldDisplaySeeMoreAttendees = useMemo(() => {
    if (attendingUsers.length - displayAttendeesCount > 0) {
      return true;
    }

    return false;
  }, [displayAttendeesCount, attendingUsers]);

  const handleDisplayMoreAttendees = () =>
    setDisplayAttendeesCount(displayAttendeesCount + DISPLAY_ATTENDEES_COUNT);

  return (
    <Box display="block" css="width: 100%;">
      <Box mb={{ _: 4, m: 5 }}>
        <WrapperEventImage src={event?.new_cover_picture?.preview_url} />
      </Box>
      <EventInformations
        actionsItems={actionsItems}
        timezoneConfiguration={timeZoneConfiguration}
      />

      <Box display="block">
        {event?.html_description && (
          <Box display="block" mt={4}>
            <Box font="h4-semibold">
              {I18n.t('web.events.user_event.details')}
            </Box>
            <WrapperDetails
              dangerouslySetInnerHTML={{
                __html: event?.html_description,
              }}
            />
          </Box>
        )}

        {event?.attending_users?.length > 0 && (
          <Box display="block" mt={4}>
            <Box font="h4-semibold">
              {__('Participants (%{attendeesCount})').i__({
                attendeesCount: event.attending_users.length.toString(),
              })}
            </Box>
            <WrapperAttendees>
              {event.attending_users
                .slice(0, displayAttendeesCount)
                .map((attendee) => (
                  <Attendee key={attendee.id} attendee={attendee} />
                ))}
              {shouldDisplaySeeMoreAttendees && (
                <Box
                  onClick={handleDisplayMoreAttendees}
                  onKeyPress={onEnterPress(handleDisplayMoreAttendees)}
                  css="cursor: pointer; width: inherit !important;"
                  mt={4}
                  color="primary"
                  fontWeight={1}
                  tabIndex={0}
                >
                  {__('See more participants')}
                </Box>
              )}
            </WrapperAttendees>
          </Box>
        )}
        {isContentRecommendationVisible && (
          <ContentRecommendation
            contentId={event.id}
            contentType={ENTITY_TYPES.userEvent}
          />
        )}
        <Box mt={8} display="block">
          <Reactions
            displayCommentsByDefault
            id={event?.id}
            key={event?.id}
            initialLikes={event?.likes}
            onTogglePostLike={toggleLike}
            railsType="Event"
            type="Event"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EventDisplay;

const EventInformations = ({ actionsItems, timezoneConfiguration }) => {
  const { event, updateEvent } = useEventContext();

  const today = new Date();
  const endDate = new Date(event?.end_date);

  const rsvpDropdownOptions = [
    { value: 'cancel', label: I18n.t('js.event.status_decline') },
    { value: 'attend', label: I18n.t('js.event.status_going') },
    { value: 'maybe_attend', label: I18n.t('js.event.status_maybe') },
  ];

  return (
    <Columns>
      <Column>
        <Box display="block">
          <Box font="h4-semibold">{I18n.t('web.events.user_event.date')}</Box>
          <Box mt={1}>
            <EventDate
              event={event}
              timezoneConfiguration={timezoneConfiguration}
            />
          </Box>
        </Box>

        {event?.locations?.[0]?.address && (
          <Box display="block" mt={4}>
            <Box font="h4-semibold">
              {I18n.t('web.events.user_event.location')}
            </Box>
            <Box mt={1}>{event.locations[0].address}</Box>
          </Box>
        )}
      </Column>

      <Column>
        <Box font="h4-semibold">
          {I18n.t('web.events.user_event.organized_by')}
        </Box>
        <Box mt={1}>
          {event?.user?.id ? (
            <UserSnapshot userId={event.user.id}>
              <Link to={Routes.user_path(event.user.id)}>
                {completeName(event.user)}
              </Link>
            </UserSnapshot>
          ) : (
            <Box font="body-semibold-asphalt">{completeName(event?.user)}</Box>
          )}
        </Box>
        <Box alignItems="flex-end">
          {!event.cancelled && endDate >= today && (
            <WrapperRsvp>
              <RsvpDropdown
                defaultValue={event?.user_status}
                options={rsvpDropdownOptions}
                eventId={event?.id}
                eventType={event?.type}
                endDate={endDate}
                onSave={(event) => {
                  updateEvent(event);
                }}
              />
            </WrapperRsvp>
          )}
          {actionsItems && (
            <WrapperDropdown>
              <DropdownThreeDotsButton
                dataTestId="landing-actions-dropdown"
                items={actionsItems}
              />
            </WrapperDropdown>
          )}
        </Box>
      </Column>
    </Columns>
  );
};

const WrapperEventImage = styled(({ src, ...otherProps }) => (
  <Box {...otherProps} />
)).attrs(({ src }) => ({
  style: {
    backgroundImage: `url(${src})`,
  },
  height: { _: 'calc((100vw - 32px) * (10 / 19))', m: 132 },
  width: { _: 'calc(100vw - 32px)', m: 255 },
}))`
  background-position: center;
  background-size: cover;
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
`;

const WrapperAttendees = styled(Box).attrs(() => ({
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  mt: -2,
}))`
  > div {
    display: block;
    width: 100%;

    ${responsive('m')`
      display: flex;
      width: 49%;
  `}
  }
`;

const WrapperDetails = styled(Box).attrs(() => ({
  mt: 1,
  display: 'block',
  maxWidth: 535,
}))`
  ${breakText}
  & a {
    color: ${({ theme }) => theme?.colors?.link};
    font-weight: 600;
    text-decoration: underline;
    text-decoration-thickness: 2px;
  }
`;

const WrapperRsvp = styled(Box).attrs({
  mt: 3,
  mr: 3,
})`
  & > div {
    width: 151px;
  }
`;

const WrapperDropdown = styled(Box).attrs({
  height: 30,
  width: 30,
  alignItems: 'center',
  justifyContent: 'center',
  mr: 3,
  p: 1,
  mt: 3,
  mb: '6px',
})``;
